import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Zap, Heart, TrendingUp, Shield } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

const VIBE_KPIS = [
  {
    icon: TrendingUp,
    title: 'Pristen stik, ne le statistika',
    text: 'Informator z dobro energijo ne le doseže 20 kontaktov na izmeno, ampak pusti pozitiven vtis o blagovni znamki IQOS/RRP. Ljudje v HoReCa okolju se odzovejo na energijo, ne na skripto.',
    metric: '20+',
    metricLabel: 'DCC / izmena',
  },
  {
    icon: Heart,
    title: 'Wibe = Stabilnost',
    text: 'Naši sodelavci ostajajo z nami, ker se pri nas počutijo slišane in cenjene. Močna ekipna kultura pomeni, da projekt INFORMERS vodimo z isto ekipo od prvega do zadnjega dne — brez nepotrebnih zamenjav in izgube znanja.',
    metric: '↑',
    metricLabel: 'Lojalnost ekipe',
  },
  {
    icon: Shield,
    title: 'Reševanje izzivov na terenu',
    text: 'Ko so odnosi v ekipi močni, se težave na terenu rešujejo s proaktivnim pristopom, ne z iskanjem krivcev. Vsak dan projekta je ekipa motivirana in usmerjena v rezultat.',
    metric: '0',
    metricLabel: 'Tolerance za izgovore',
  },
];

export const WibeFactorSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).wibeFactor;
  const ICONS = [TrendingUp, Heart, Shield];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.wibe-card',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.15,
          scrollTrigger: { trigger: '.wibe-cards', start: 'top 80%' } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="wibe-factor"
      ref={sectionRef}
      data-testid="wibe-factor-section"
      className="scroll-section relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-[#C87533]/6 blur-[120px]" />
      </div>

      <div className="section-content max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header – kompaktno */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/10">
            <Zap className="w-3.5 h-3.5 text-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            {tx.headline} <span className="text-copper-gradient">{tx.headlineAccent}</span>
          </h2>
        </div>

        {/* Dve koloni: kartice + manifest */}
        <div className="grid lg:grid-cols-5 gap-6 items-start">

          {/* Levo: 3 kompaktne kartice */}
          <div className="wibe-cards lg:col-span-3 grid grid-cols-1 gap-4">
            {tx.cards.map((item, i) => {
              const Icon = ICONS[i];
              return (
                <div key={i} className="wibe-card rounded-xl border border-white/8 bg-white/3 hover:border-[#C87533]/25 transition-all duration-300 p-5 flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#C87533]/15 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#C87533]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className="font-display text-base font-bold text-white">{item.title}</h3>
                      <div className="text-right flex-shrink-0 ml-3">
                        <span className="font-display text-xl font-bold text-[#C87533]">{item.metric}</span>
                        <span className="font-mono text-[10px] text-white/30 uppercase ml-1">{item.metricLabel}</span>
                      </div>
                    </div>
                    <p className="text-sm text-white/55 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desno: manifest citat */}
          <div className="lg:col-span-2 relative rounded-xl overflow-hidden border border-[#C87533]/20 bg-gradient-to-br from-[#C87533]/8 via-transparent to-transparent p-7 h-full flex flex-col justify-between">
            <div className="font-mono text-5xl text-[#C87533]/20 leading-none mb-4 select-none">"</div>
            <p className="font-display text-lg lg:text-xl text-white font-medium leading-relaxed flex-1">
              {tx.quote}
            </p>
            <div className="flex items-center gap-3 mt-6">
              <div className="w-6 h-px bg-[#C87533]/50" />
              <span className="font-mono text-sm text-[#C87533]">{tx.quoteAuthor}</span>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
